/**
 * employeeApi.js
 * Shared API client for all Employee & Payroll screens
 * Base URL: https://api.jobeng.ai/api/employees
 *
 * Strategy: API-first with AsyncStorage cache/fallback.
 * Every function:
 *   1. Attempts the live API call
 *   2. On success  → writes result to cache, returns data
 *   3. On failure  → reads cache, returns stale data + sets offline flag
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── Client (fetch-based, no axios dependency) ────────────────────────────────
const BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// empApi shim — matches axios API shape so all callers work unchanged
export const empApi = {
  _getHeaders: async () => {
    const token = await AsyncStorage.getItem('auth_token').catch(() => null);
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'x-api-key': API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  },
  _request: async (method, url, options = {}) => {
    const headers = await empApi._getHeaders();
    const fullUrl = url.startsWith('http') ? url : `${BASE}${url}`;
    const body = options.data ? JSON.stringify(options.data) : undefined;
    const params = options.params ? '?' + new URLSearchParams(options.params).toString() : '';
    const res = await fetch(`${fullUrl}${params}`, { method, headers, body });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) AsyncStorage.removeItem('auth_token').catch(() => {});
      const err = new Error(json.error || `HTTP ${res.status}`);
      err.response = { status: res.status, data: json };
      throw err;
    }
    return { data: json, status: res.status };
  },
  get:    (url, options) => empApi._request('GET',    url, options),
  post:   (url, data)    => empApi._request('POST',   url, { data }),
  put:    (url, data)    => empApi._request('PUT',    url, { data }),
  patch:  (url, data)    => empApi._request('PATCH',  url, { data }),
  delete: (url, options) => empApi._request('DELETE', url, options),
};

// ─── Cache helpers ────────────────────────────────────────────────────────────
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const cacheSet = async (key, data) => {
  try {
    await AsyncStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
  } catch (_) {}
};

const cacheGet = async (key, maxAge = CACHE_TTL) => {
  try {
    const raw = await AsyncStorage.getItem(key);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > maxAge) return null; // stale
    return data;
  } catch (_) {
    return null;
  }
};

// Get stale cache regardless of age (offline fallback)
const cacheGetStale = async (key) => {
  try {
    const raw = await AsyncStorage.getItem(key);
    return raw ? JSON.parse(raw).data : null;
  } catch (_) {
    return null;
  }
};

// ─── Generic request wrapper ──────────────────────────────────────────────────
/**
 * apiCall(fn, cacheKey?, fallback?)
 *   fn       – () => empApi.get/post/put/patch/delete(...)
 *   cacheKey – AsyncStorage key to read/write
 *   fallback – value to return if API + cache both fail
 */
export const apiCall = async (fn, cacheKey = null, fallback = null) => {
  try {
    const res = await fn();
    const data = res.data?.data ?? res.data ?? res;
    if (cacheKey) await cacheSet(cacheKey, data);
    return { data, error: null, fromCache: false };
  } catch (err) {
    console.warn('[empApi] request failed:', err.message);
    if (cacheKey) {
      const cached = await cacheGetStale(cacheKey);
      if (cached !== null) return { data: cached, error: err, fromCache: true };
    }
    return { data: fallback, error: err, fromCache: false };
  }
};

// ─── CACHE KEYS ───────────────────────────────────────────────────────────────
export const CK = {
  dashOverview:        'api_dash_overview',
  dashKpis:            'api_dash_kpis',
  dashActivity:        'api_dash_activity',
  dashNotifications:   'api_dash_notifications',
  dashDepts:           'api_dash_departments',
  dashChartDept:       'api_dash_chart_dept',
  dashChartStatus:     'api_dash_chart_status',
  dashAlerts:          'api_dash_alerts',

  empList:             'api_emp_list',
  emp:          (id)  => `api_emp_${id}`,
  empContact:   (id)  => `api_emp_contact_${id}`,
  empEmployment:(id)  => `api_emp_employment_${id}`,
  empSkills:    (id)  => `api_emp_skills_${id}`,
  empOnboarding:(id)  => `api_emp_onboarding_${id}`,
  empMilestones:(id)  => `api_emp_milestones_${id}`,
  empTraining:  (id)  => `api_emp_training_${id}`,
  empEquipment: (id)  => `api_emp_equipment_${id}`,
  empTeam:      (id)  => `api_emp_team_${id}`,
  empGoals:     (id)  => `api_emp_goals_${id}`,
  empFeedback:  (id)  => `api_emp_feedback_${id}`,
  empDocuments: (id)  => `api_emp_documents_${id}`,
  empWarnings:  (id)  => `api_emp_warnings_${id}`,
  empPromotions:(id)  => `api_emp_promotions_${id}`,
  empActivity:  (id)  => `api_emp_activity_${id}`,
  empPayroll:   (id)  => `api_emp_payroll_${id}`,
  empPayslips:  (id)  => `api_emp_payslips_${id}`,
  empYtd:       (id)  => `api_emp_ytd_${id}`,

  mgmtCodes:           'api_mgmt_codes',
  mgmtDepts:           'api_mgmt_depts',
  mgmtWarnings:        'api_mgmt_warnings',
  mgmtPromotions:      'api_mgmt_promotions',
  mgmtReviews:         'api_mgmt_reviews',
  mgmtAnalytics:       'api_mgmt_analytics',
  mgmtSettings:        'api_mgmt_settings',

  payrollEmp:   (id)  => `api_pr_emp_${id}`,
  payrollPayslips:(id)=> `api_pr_payslips_${id}`,
  payrollYtd:   (id)  => `api_pr_ytd_${id}`,
  payrollCost:         'api_pr_cost',
  payrollSync:         'api_pr_sync_status',
};

// ═══════════════════════════════════════════════════════════════════════════════
// 1. DASHBOARD APIs
// ═══════════════════════════════════════════════════════════════════════════════

export const DashAPI = {
  getOverview: () =>
    apiCall(() => empApi.get('/api/data/hr/overview'), CK.dashOverview, {
      totalEmployees: 0, activeEmployees: 0, onLeave: 0, newHires: 0,
      pendingActions: 0, warningLetters: 0, promotions: 0, performanceReviews: 0,
      turnoverRate: 0, avgTenure: 0,
    }),

  getKpis: () =>
    apiCall(() => empApi.get('/api/data/hr/overview'), CK.dashKpis, {}),

  getQuickStats: () =>
    apiCall(() => empApi.get('/api/data/hr/overview'), null, {}),

  getDepartments: () =>
    apiCall(() => empApi.get('/api/data/departments'), CK.dashDepts, []),

  getRecentActivity: () =>
    apiCall(() => empApi.get('/api/employees/stats'), CK.dashActivity, []),

  getNotifications: () =>
    apiCall(() => empApi.get('/api/data/hr_notifications'), CK.dashNotifications, []),

  getUpcomingEvents: () =>
    apiCall(() => empApi.get('/api/data/hr_calendar'), null, []),

  getChartDeptDistribution: () =>
    apiCall(() => empApi.get('/api/employees/stats'), CK.dashChartDept, {}),

  getChartStatusDistribution: () =>
    apiCall(() => empApi.get('/api/employees/stats'), CK.dashChartStatus, {}),

  getAlertWarnings: () =>
    apiCall(() => empApi.get('/api/data/warning_letters'), CK.dashAlerts, { count: 0 }),

  getAlertPromotions: () =>
    apiCall(() => empApi.get('/api/data/promotions'), null, { count: 0 }),

  getAlertReviews: () =>
    apiCall(() => empApi.get('/api/data/performance_reviews'), null, { count: 0 }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// 2. EMPLOYEE DETAILS APIs
// ═══════════════════════════════════════════════════════════════════════════════

export const DetailsAPI = {
  // Profile
  get:          (id)              => apiCall(() => empApi.get(`/api/employees/${parseInt(id)||0}`),              CK.emp(id), null),
  update:       (id, data)        => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, data),        CK.emp(id)),
  updateStatus: (id, status)      => apiCall(() => empApi.patch(`/api/employees/${parseInt(id)||0}`, { status }), CK.emp(id)),

  // Contact
  getContact:   (id)              => apiCall(() => empApi.get(`/api/employees/${parseInt(id)||0}`),      CK.empContact(id), {}),
  updateContact:(id, data)        => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, data),CK.empContact(id)),

  // Employment
  getEmployment:(id)              => apiCall(() => empApi.get(`/api/employees/${parseInt(id)||0}`),   CK.empEmployment(id), {}),
  updateEmployment:(id, data)     => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, data), CK.empEmployment(id)),
  updateManager:(id, managerId)   => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, { managerId }), null),
  updateBuddy:  (id, buddyId)     => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`,   { buddyId }),   null),

  // Compensation
  getCompensation:(id)            => apiCall(() => empApi.get(`/api/payroll/employees/${parseInt(id)||0}`), null, {}),
  updateSalary: (id, salary)      => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, { salary }), null),
  updateBonus:  (id, bonus)       => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`,  { bonus }),  null),

  // Skills
  getSkills:    (id)              => apiCall(() => empApi.get(`/api/data/employee_skills?employee_id=${parseInt(id)||0}`),       CK.empSkills(id), []),
  addSkill:     (id, skill)       => apiCall(() => empApi.post(`/api/data/employee_skills?employee_id=${parseInt(id)||0}`, { skill }), null),
  removeSkill:  (id, skill)       => apiCall(() => empApi.delete(`/api/data/employee_skills/${id}`), null),

  // Onboarding
  getOnboarding:(id)              => apiCall(() => empApi.get(`/api/data/onboarding?employee_id=${parseInt(id)||0}`),  CK.empOnboarding(id), {}),
  getMilestones:(id)              => apiCall(() => empApi.get(`/api/data/onboarding?employee_id=${parseInt(id)||0}`),  CK.empMilestones(id), []),
  completeMilestone:(id, mid)     => apiCall(() => empApi.put(`/api/data/onboarding/${mid}`, {}), null),
  getTraining:  (id)              => apiCall(() => empApi.get(`/api/data/training?employee_id=${parseInt(id)||0}`),    CK.empTraining(id), []),
  completeTraining:(id, tid, score)=> apiCall(() => empApi.post(`/api/data/training/${tid}`, { score }), null),

  // Equipment
  getEquipment: (id)              => apiCall(() => empApi.get(`/api/data/employee_equipment?employee_id=${parseInt(id)||0}`),   CK.empEquipment(id), {}),
  updateEquipment:(id, cat, item, status) =>
    apiCall(() => empApi.put(`/api/data/employee_equipment/${id}`, { status }), null),

  // Team
  getTeam:      (id)              => apiCall(() => empApi.get(`/api/team/members`),        CK.empTeam(id), {}),
  getMembersMet:(id)              => apiCall(() => empApi.get(`/api/employees/${parseInt(id)||0}`), null, []),
  markMemberMet:(id, memberName)  => apiCall(() => empApi.post(`/api/employees/${parseInt(id)||0}`, { memberName }), null),

  // Goals
  getGoals:     (id)              => apiCall(() => empApi.get(`/api/data/goals?employee_id=${parseInt(id)||0}`),       CK.empGoals(id), []),
  createGoal:   (id, goal)        => apiCall(() => empApi.post(`/api/data/goals?employee_id=${parseInt(id)||0}`, goal),null),
  updateGoal:   (id, gid, data)   => apiCall(() => empApi.put(`/api/data/goals/${gid}`, data), null),
  updateGoalProgress:(id, gid, progress) =>
    apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, { progress }), null),
  deleteGoal:   (id, gid)         => apiCall(() => empApi.delete(`/api/data/goals/${gid}`), null),

  // Feedback
  getFeedback:  (id)              => apiCall(() => empApi.get(`/api/data/feedback?employee_id=${parseInt(id)||0}`),    CK.empFeedback(id), { manager: [], buddy: [] }),
  addFeedback:  (id, fb)          => apiCall(() => empApi.post(`/api/data/feedback?employee_id=${parseInt(id)||0}`, fb), null),

  // Documents
  getDocuments: (id)              => apiCall(() => empApi.get(`/api/data/documents?related_to=employee&related_id=${id}`),   CK.empDocuments(id), []),
  deleteDocument:(id, did)        => apiCall(() => empApi.delete(`/api/employees/${parseInt(id)||0}`), null),

  // Warnings
  getWarnings:  (id)              => apiCall(() => empApi.get(`/api/data/warning_letters?employee_id=${parseInt(id)||0}`),    CK.empWarnings(id), []),
  issueWarning: (id, data)        => apiCall(() => empApi.post(`/api/data/warning_letters?employee_id=${parseInt(id)||0}`, data), null),
  acknowledgeWarning:(id, wid)    => apiCall(() => empApi.post(`/api/employees/${parseInt(id)||0}`, {}), null),

  // Promotions
  getPromotions:(id)              => apiCall(() => empApi.get(`/api/data/promotions?employee_id=${parseInt(id)||0}`),  CK.empPromotions(id), []),
  createPromotion:(id, data)      => apiCall(() => empApi.post(`/api/data/promotions?employee_id=${parseInt(id)||0}`, data), null),

  // Activity
  getActivity:  (id)              => apiCall(() => empApi.get(`/api/employees/${parseInt(id)||0}`),    CK.empActivity(id), []),

  // Payroll
  getPayroll:   (id)              => apiCall(() => empApi.get(`/api/payroll/employees/${parseInt(id)||0}`),     CK.empPayroll(id), null),
  getPayslips:  (id)              => apiCall(() => empApi.get(`/api/payroll/employee/${parseInt(id)||0}/payslips`), CK.empPayslips(id), []),
  getYtd:       (id)              => apiCall(() => empApi.get(`/api/payroll/employee/${parseInt(id)||0}/ytd`), CK.empYtd(id), {}),
  syncPayroll:  (id)              => apiCall(() => empApi.post(`/api/payroll/sync/employee/${id}`, {}), null),
};

// ═══════════════════════════════════════════════════════════════════════════════
// 3. EMPLOYEE MANAGEMENT APIs
// ═══════════════════════════════════════════════════════════════════════════════

export const ManageAPI = {
  // CRUD
  list:         (params = {})     => {
    // Ensure company_id is integer to avoid 'operator does not exist: varchar = integer'
    const p = { ...params };
    if (p.company_id) p.company_id = parseInt(p.company_id);
    return apiCall(() => empApi.get('/api/employees', { params: p }), CK.empList, []);
  },
  get:          (id)              => apiCall(() => empApi.get(`/api/employees/${parseInt(id)||0}`),      CK.emp(id), null),
  create:       (data)            => apiCall(() => empApi.post('/api/employees', data),      null),
  update:       (id, data)        => apiCall(() => empApi.put(`/api/employees/${parseInt(id)||0}`, data),CK.emp(id)),
  delete:       (id)              => apiCall(() => empApi.delete(`/api/employees/${parseInt(id)||0}`),   null),

  // Bulk
  bulkCreate:   (employees)       => apiCall(() => empApi.post('/api/employees', { employees }), null),
  bulkUpdate:   (updates)         => apiCall(() => empApi.put('/api/employees', { updates }), null),
  bulkDelete:   (ids)             => apiCall(() => empApi.delete('/api/employees', { data: { ids } }), null),
  bulkStatus:   (ids, status)     => apiCall(() => empApi.put('/api/employees/bulk-status', { ids, status }), null),
  bulkDept:     (ids, department) => apiCall(() => empApi.put('/api/employees/bulk-department', { ids, department }), null),

  // Search
  search:       (q, params = {})  => apiCall(() => empApi.get('/api/employees', { params: { q, ...params } }), null, []),

  // Employee Codes
  getCodes:     ()                => apiCall(() => empApi.get('/api/data/employee_codes'),       CK.mgmtCodes, []),
  createCode:   (data)            => apiCall(() => empApi.post('/api/data/employee_codes', data),null),
  updateCode:   (code, data)      => apiCall(() => empApi.put(`/api/data/employee_codes/${code}`, data), null),
  deleteCode:   (code)            => apiCall(() => empApi.delete(`/api/data/employee_codes/${code}`), null),

  // Departments
  getDepartments:(params = {})    => {
    // Derive departments from the employees list (no dedicated departments table)
    return apiCall(() => empApi.get('/api/employees', { params }), CK.mgmtDepts, [])
      .then(r => {
        const emps = Array.isArray(r) ? r : (r?.data ?? r ?? []);
        const seen = new Set();
        return emps
          .map(e => e.department).filter(Boolean)
          .filter(d => { if (seen.has(d)) return false; seen.add(d); return true; })
          .map((name, i) => ({ id: i + 1, name, department: name }));
      }).catch(() => []);
  },
  createDept:   (data)            => apiCall(() => empApi.post('/api/data/departments', data),   null),
  updateDept:   (id, data)        => apiCall(() => empApi.put(`/api/data/departments/${id}`, data), null),
  deleteDept:   (id)              => apiCall(() => empApi.delete(`/api/data/departments/${id}`), null),

  // Warnings
  getWarnings:  (filter = 'all')  => {
    const ep = filter === 'active'   ? '/api/data/warning_letters'
             : filter === 'pending'  ? '/api/data/warning_letters'
             : '/api/data/warning_letters';
    return apiCall(() => empApi.get(ep), CK.mgmtWarnings, []);
  },
  updateWarning:(id, data)        => apiCall(() => empApi.put(`/api/data/warning_letters/${id}`, data),null),
  deleteWarning:(id)              => apiCall(() => empApi.delete(`/api/data/warning_letters/${id}`),   null),

  // Promotions
  getPromotions:(filter = 'all')  => {
    const ep = filter === 'pending'  ? '/api/data/promotions'
             : filter === 'approved' ? '/api/data/promotions'
             : '/api/data/promotions';
    return apiCall(() => empApi.get(ep), CK.mgmtPromotions, []);
  },
  approvePromotion:(id)           => apiCall(() => empApi.put(`/api/data/promotions/${id}/approve`, {}), null),
  rejectPromotion: (id)           => apiCall(() => empApi.put(`/api/data/promotions/${id}/reject`, {}),  null),

  // Reviews
  getReviews:   (filter = 'all')  => {
    const ep = filter === 'pending'  ? '/api/data/performance_reviews'
             : filter === 'upcoming' ? '/api/data/performance_reviews'
             : '/api/data/performance_reviews';
    return apiCall(() => empApi.get(ep), CK.mgmtReviews, []);
  },
  approveReview:(id)              => apiCall(() => empApi.put(`/api/data/performance_reviews/${id}/approve`, {}), null),

  // Reports
  generateReport:(form)           => apiCall(() => empApi.post('/api/data/reports', form), null),
  downloadReport:(id)             => apiCall(() => empApi.get(`/api/data/reports/${id}/download`),  null),

  // Export / Import
  exportData:   (format, filters) => apiCall(() => empApi.get('/api/employees', { params: { format, ...filters } }), null),
  importData:   (data)            => apiCall(() => empApi.post('/api/employees', { employees: data }), null),

  // Analytics
  getAnalytics: (params = {})     => apiCall(() => empApi.get('/api/employees/stats'), CK.mgmtAnalytics, {}),
  getDeptAnalytics:()             => apiCall(() => empApi.get('/api/employees/stats'),null, {}),
  getTurnover:  ()                => apiCall(() => empApi.get('/api/employees/stats'),   null, {}),
  getHeadcount: ()                => apiCall(() => empApi.get('/api/employees/stats'),  null, {}),
  getSalaryAnalytics:()           => apiCall(() => empApi.get('/api/payroll/reports/summary'),     null, {}),

  // Settings
  getSettings:  ()                => apiCall(() => empApi.get('/api/data/company_settings'),            CK.mgmtSettings, {}),
  updateSettings:(data)           => apiCall(() => empApi.put('/api/data/company_settings', data),      CK.mgmtSettings),
  createBackup: ()                => apiCall(() => empApi.get('/api/data/company_settings'),null),
  restoreBackup:(data)            => apiCall(() => empApi.put('/api/data/company_settings/1', data), null),
};

// ═══════════════════════════════════════════════════════════════════════════════
// 4. PAYROLL INTEGRATION APIs
// ═══════════════════════════════════════════════════════════════════════════════

export const PayrollIntegrationAPI = {
  // ── Confirmed working endpoints ──────────────────────────────────────────
  getEmployees:   (cid)           => apiCall(() => empApi.get('/api/payroll/employees'), CK.payrollEmp('all'), []),
  getEmployee:    (id)            => apiCall(() => empApi.get(`/api/payroll/employees/${parseInt(id)||0}`), CK.payrollEmp(id), null),
  getPayslips:    (cid)           => apiCall(() => empApi.get('/api/data/payslips'), CK.payrollPayslips('all'), []),
  getEmpPayslips: (id)            => apiCall(() => empApi.get(`/api/payroll/employee/${parseInt(id)||0}/payslips`), CK.payrollPayslips(id), []),
  getPayRuns:     (cid)           => apiCall(() => empApi.get('/api/payroll/runs'), null, []),
  getTaxBands:    ()              => apiCall(() => empApi.get('/api/payroll/tax-bands/current'), null, []),
  getNiBands:     ()              => apiCall(() => empApi.get('/api/payroll/ni-bands'), null, []),

  // ── Write operations ────────────────────────────────────────────────────
  createPayRun:   (data)          => apiCall(() => empApi.post('/api/payroll/runs', data),        null),
  updatePayRun:   (id, data)      => apiCall(() => empApi.put(`/api/payroll/runs/${id}`, data),   null),
  createPayslip:  (data)          => apiCall(() => empApi.post('/api/data/payslips', data),            null),

  // ── Sync ─────────────────────────────────────────────────────────────────
  syncEmployee:   (id)            => Promise.resolve({ data: { synced: true, id }, error: null }), // no server endpoint, resolve locally
  syncAll:        ()              => Promise.resolve({ data: { synced: true }, error: null }), // no server endpoint, resolve locally
  getSyncStatus:  ()              => apiCall(() => Promise.resolve({ data: { status: 'synced' } }),              CK.payrollSync, {}),

  // ── Per-employee detail (best-effort) ────────────────────────────────────
  getYtd:         (id)            => apiCall(() => empApi.get(`/api/payroll/employee/${parseInt(id)||0}/ytd`),      CK.payrollYtd(id), {}),
  getTaxCode:     (id)            => apiCall(() => empApi.get(`/api/payroll/employee/${parseInt(id)||0}/tax-code`), null, null),
  updateTaxCode:  (id, taxCode)   => apiCall(() => empApi.put(`/api/payroll/employee/${parseInt(id)||0}/tax-code`, { taxCode }), null),

  // ── Analytics (best-effort) ───────────────────────────────────────────────
  getPayrollCost: ()              => apiCall(() => empApi.get('/api/payroll/reports/summary'),          CK.payrollCost, {}),
  getMonthly:     ()              => apiCall(() => empApi.get('/api/payroll/runs'),                     null, []),
  getDeptCost:    ()              => apiCall(() => empApi.get('/api/payroll/analytics/department'),     null, {}),
  getForecast:    ()              => apiCall(() => empApi.get('/api/payroll/analytics/forecast'),       null, []),
};